import React, { FC } from "react"
import { RoundedRectangle } from "../../atoms/roundedrectangle/RoundedRectangle"
import { RRIconBar } from "../../atoms/roundedrectangle/RRIconBar"
import { Spacer, StatusBubble } from "../../atoms/Atoms"
import { H3 } from "@carrotfertility/carotene"
import { BodySmall } from "../../atoms/Bodies"
import { ArrowLink } from "../../atoms/links/ArrowLink"
import { Button } from "components/views/atoms/buttons/Button"
import { DetailsList, DetailsListItem } from "components/views/molecules/DetailsList"
import { useDidMount, useToggle } from "utils/Hooks"
import { CarrotCardModuleProps } from "components/views/molecules/CarrotCardModuleContainer"
import { fetchCardDetails } from "../../../../actions"
import { useDispatch, useSelector } from "react-redux"
import { getCardDetails } from "reducers/cardDetails"
import styled from "styled-components"
import { useLocation } from "react-router-dom"
import Helpers from "../../../../utils/Helpers"
import { SupportCenterLinks } from "../../../../utils/SupportCenterLinks"
import { DisallowWhilePosing } from "../../../../utils/DisallowWhilePosing"
import { ZendeskArrowLink } from "../../atoms/links/ZendeskArrowLink"
import { FormattedDate, FormattedMessage, useIntl } from "react-intl"

const StyledButton = styled(Button)`
  width: 191px;
  text-align: center;
`

const VirtualCardDetailsAccordion = () => {
  const location = useLocation()
  const [showCardDetails, toggleShowCardDetails] = useToggle(false)
  const dispatch = useDispatch()
  const cardDetails = useSelector(getCardDetails)

  useDidMount(() => {
    const cardDetailsShouldStartOpen = location.hash === "#card-details"
    toggleShowCardDetails(cardDetailsShouldStartOpen)
    dispatch(fetchCardDetails())
  })

  return (
    <>
      <Spacer height={1.5} />
      <StyledButton
        onClick={() => {
          !showCardDetails && dispatch(fetchCardDetails())
          toggleShowCardDetails()
        }}
        outline
        horizontalPadding={3}
      >
        {showCardDetails && !cardDetails.isLoaded && <FormattedMessage defaultMessage="Loading..." />}
        {showCardDetails && cardDetails.isLoaded && <FormattedMessage defaultMessage="Hide card details" />}
        {!showCardDetails && <FormattedMessage defaultMessage="Show card details" />}
      </StyledButton>
      {showCardDetails && cardDetails.isLoaded && <ShowCardDetails cardDetails={cardDetails.cardDetails} />}
    </>
  )
}

const CarrotCardActiveModule: FC<CarrotCardModuleProps> = ({ backgroundColor, isVirtualCard }) => {
  const intl = useIntl()
  return (
    <RoundedRectangle backgroundColor={backgroundColor}>
      <RRIconBar
        iconName="icn-carrot-cards"
        iconBackgroundColor="white-60"
        label={
          <StatusBubble backgroundColor="bright-green" status={intl.formatMessage({ defaultMessage: "Active" })} />
        }
      />
      <H3 variant="primary" stack="tiny">
        <FormattedMessage defaultMessage="Carrot Card®" />
      </H3>
      <BodySmall color="black-80">
        {isVirtualCard ? (
          <FormattedMessage defaultMessage="Your virtual card is now active. Learn about how it works and what to do if a transaction is declined." />
        ) : (
          <FormattedMessage defaultMessage="Your card is now active. Learn about how it works and what to do if a transaction is declined." />
        )}
      </BodySmall>
      <Spacer height={0.5} />
      <ZendeskArrowLink supportCenterLink={SupportCenterLinks.CARROT_CARD_SUPPORT_CENTER}>
        <FormattedMessage defaultMessage="How to use your Carrot Card" />
      </ZendeskArrowLink>
      {!isVirtualCard && (
        <>
          <Spacer height={0.25} />
          <ArrowLink to="/talk-to-carrot/sendmessage/stolen">
            <FormattedMessage defaultMessage="Report lost or stolen card" />
          </ArrowLink>
        </>
      )}

      {isVirtualCard && (
        <DisallowWhilePosing>
          <VirtualCardDetailsAccordion />
        </DisallowWhilePosing>
      )}
    </RoundedRectangle>
  )
}

type ShowCardDetailsProps = {
  cardDetails: any
}

type ShowRetrievedCardDetailsProps = {
  cardDetails: any
}

export const ShowCardDetails: FC<ShowCardDetailsProps> = ({ cardDetails }) => {
  return (
    <>
      <Spacer height={1.5} />
      {cardDetails ? <ShowRetrievedCardDetails cardDetails={cardDetails} /> : <ShowErrorRetrievingCardDetails />}
    </>
  )
}

export const ShowRetrievedCardDetails: FC<ShowRetrievedCardDetailsProps> = ({ cardDetails }) => {
  const address = cardDetails.billingAddress
  const formattedCreditCardNumber = Helpers.formatCreditCardNumber(cardDetails.number)
  const intl = useIntl()

  return (
    <DetailsList>
      <DetailsListItem header={intl.formatMessage({ defaultMessage: "Cardholder name" })} body={cardDetails.name} />
      <DetailsListItem header={intl.formatMessage({ defaultMessage: "Type" })} body="Visa" />
      <DetailsListItem
        header={intl.formatMessage({ defaultMessage: "Card number" })}
        body={formattedCreditCardNumber}
      />
      <DetailsListItem
        header={intl.formatMessage({ defaultMessage: "Expiration Date" })}
        body={
          <FormattedDate
            value={new Date(cardDetails.expirationYear, cardDetails.expirationMonth - 1)}
            year="numeric"
            month="2-digit"
          />
        }
      />
      <DetailsListItem header={intl.formatMessage({ defaultMessage: "SECURITY CODE CVV" })} body={cardDetails.cvc} />
      <DetailsListItem
        header={intl.formatMessage({ defaultMessage: "Billing address" })}
        body={
          <span>
            {address.line1}
            {address.line2 != null && (
              <>
                <br />
                {address.line2}
              </>
            )}
            <br />
            {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
            {address.city}, {address.state} {address.postalCode}
            <br />
            {address.country}
          </span>
        }
      />
    </DetailsList>
  )
}

export const ShowErrorRetrievingCardDetails = () => (
  <DetailsList>
    <BodySmall color="black-80">
      <FormattedMessage defaultMessage="We're having trouble retrieving your card details." />
    </BodySmall>
    <Spacer height={1} />
    <ArrowLink onClick={() => window.location.reload()}>
      <FormattedMessage defaultMessage="Refresh the page" />
    </ArrowLink>
    <ArrowLink to="/talk-to-carrot">
      <FormattedMessage defaultMessage="Contact the Care Team" />
    </ArrowLink>
  </DetailsList>
)

export { CarrotCardActiveModule }
